import { useState } from 'react';
import { useTranslator } from 'tradera/lang/translate';
import Flag from 'tradera/components/flag/flag';
import { classNames } from 'tradera/utils/class-names';
import { SearchInput } from 'tradera/components/form-controls/search-input/search-input';
import { DropdownButton } from 'tradera/components/dropdown-button/dropdown-button';

import styles from './shipping-country-select-dropdown.module.scss';

type ShippingCountry = {
    name: string;
    countryCodeIso2: string;
};

type ShippingCountrySelectDropdownProps = {
    shippingCountries: ShippingCountry[];
    selectedShippingCountryCodeIso2: string;
    onSelect: (countryCodeIso2: string) => void;
    className?: string;
    itemsBeforeScrollCount?: number;
    shippingCountryPlaceholder?: string;
    relative?: boolean;
    style?: React.CSSProperties;
};

export const ShippingCountrySelectDropdown = ({
    shippingCountries,
    selectedShippingCountryCodeIso2,
    onSelect,
    className,
    itemsBeforeScrollCount = 5,
    shippingCountryPlaceholder,
    relative,
    style = {}
}: ShippingCountrySelectDropdownProps) => {
    const { t } = useTranslator();
    const [search, setSearch] = useState('');
    const [isSearchEnabled, setIsSearchEnabled] = useState(false);
    const includesSearch = (v: string) =>
        v.toLowerCase().includes(search.toLowerCase());
    const filteredShippingCountries = shippingCountries.filter(
        ({ name, countryCodeIso2 }) =>
            includesSearch(name) || includesSearch(countryCodeIso2)
    );
    const selectedShippingCountryName = shippingCountries.find(
        (x) =>
            x.countryCodeIso2 === selectedShippingCountryCodeIso2.toLowerCase()
    )?.name;

    const paddingHeight = 7;
    const listLength =
        filteredShippingCountries.length === 0
            ? 1
            : filteredShippingCountries.length;
    const minOfListOrItemsBeforeScrollCount = Math.min(
        listLength,
        itemsBeforeScrollCount
    );
    const height =
        paddingHeight +
        minOfListOrItemsBeforeScrollCount * 35 +
        (listLength <= itemsBeforeScrollCount ? 0 : 21);
    const listStyle: React.CSSProperties = {
        height: `${height}px`,
        width: '100%',
        zIndex: 1,
        overflowY: 'auto',
        ...style
    };

    return (
        <div className={className}>
            {!isSearchEnabled ? (
                <DropdownButton
                    fluid
                    onClick={() => {
                        setIsSearchEnabled(true);
                    }}
                    icon={
                        <Flag
                            className={styles.shippingCountryFlag}
                            countryCodeIso2={selectedShippingCountryCodeIso2}
                        />
                    }>
                    {selectedShippingCountryName}
                </DropdownButton>
            ) : (
                <SearchInput
                    autoFocus
                    className="w-100"
                    name="shippingCountry"
                    placeholder={
                        shippingCountryPlaceholder ||
                        t('siteHeader_shippingCountryPlaceholder')
                    }
                    aria-label={
                        shippingCountryPlaceholder ||
                        t('siteHeader_shippingCountryPlaceholder')
                    }
                    value={search}
                    autoComplete="off"
                    onChange={(e) => setSearch(e.target.value)}
                    onBlur={() => {
                        setTimeout(() => {
                            setIsSearchEnabled(false);
                        }, 100);
                    }}
                    onClear={() => setIsSearchEnabled(false)}
                />
            )}
            {isSearchEnabled && (
                <div className={classNames(relative && 'position-relative')}>
                    <div
                        className="bg-white pt-1 position-absolute shadow"
                        onMouseDown={(e) => e.preventDefault()}
                        style={listStyle}>
                        <div className="list-group w-100">
                            {filteredShippingCountries.length > 0 ? (
                                <>
                                    {filteredShippingCountries.map(
                                        (shippingCountry) => (
                                            <div
                                                key={
                                                    shippingCountry.countryCodeIso2
                                                }
                                                className="d-flex align-items-center list-group-item list-group-item-action px-2 cursor-pointer text-truncate"
                                                onClick={() => {
                                                    setIsSearchEnabled(false);
                                                    onSelect(
                                                        shippingCountry.countryCodeIso2
                                                    );
                                                }}>
                                                <Flag
                                                    className={classNames(
                                                        styles.shippingCountryFlag,
                                                        'mr-1'
                                                    )}
                                                    countryCodeIso2={
                                                        shippingCountry.countryCodeIso2
                                                    }
                                                />
                                                {shippingCountry.name}
                                            </div>
                                        )
                                    )}
                                </>
                            ) : (
                                <div className="d-flex list-group-item list-group-item-action">
                                    {t('siteHeader_shippingCountryNoResults')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
